import { type FC, useCallback, useMemo } from 'react';
import { Button, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import { useStockAvailability } from '../../contexts/stock-availability.context';
import { useTranslation } from 'next-i18next';
import { type CartItem, CartItemTypeEnum } from '../../models/cart.model';

import styles from './cart-item-stock-availability.module.css';
import { useCartActionsV3 } from '../../hooks';
import type { ConfigurableCartItem } from '../../types';

interface CartItemStockAvailabilityProps {
    item?: CartItem | null;
}

/**
 * CART ITEM STOCK AVAILABILITY COMPONENT
 *
 * Displays stock availability message for cart items.
 *
 * ARCHITECTURE:
 * - Uses stock data from StockAvailabilityContext (provided by parent CartItems)
 * - No individual query - data is pre-fetched in batch by parent
 * - Shows detailed stock information: unavailable, low stock, errors
 *
 * USAGE:
 * Must be used within CartItems component tree that provides stock context.
 */
export const CartItemStockAvailability: FC<CartItemStockAvailabilityProps> = ({ item }) => {
    const { t } = useTranslation('quote');
    const { handleUpdateQuantity } = useCartActionsV3();

    const isConfigurableItem = useMemo(() => {
        return (
            item?.__typename === CartItemTypeEnum.CONFIGURABLE ||
            item?.__typename === CartItemTypeEnum.CONFIGURABLE_LOCAL
        );
    }, [item?.__typename]);

    // Extract SKU from cart item
    const sku = useMemo(() => {
        if (isConfigurableItem) {
            return (item as ConfigurableCartItem)?.configured_variant?.sku;
        }
        return item?.product?.sku;
    }, [isConfigurableItem, item]);

    // GET STOCK DATA FROM CONTEXT: Pre-fetched by parent CartItems
    const { isSalable, availableQuantity, errors } = useStockAvailability(sku ?? undefined);

    const getInsufficientStockMessage = useCallback(() => {
        if (availableQuantity === null || availableQuantity <= 0) {
            return '';
        }

        return t('text_stock_insufficient', {
            available: availableQuantity,
            requested: item?.quantity ?? 0,
        });
    }, [availableQuantity, item?.quantity, t]);

    const getErrorMessage = useCallback(() => {
        if (!errors || errors.length === 0) {
            return '';
        }

        const firstError = errors[0];

        if (firstError.code === 'is_salable_with_reservations-not_enough_qty') {
            return getInsufficientStockMessage();
        }

        return firstError.message;
    }, [errors, getInsufficientStockMessage]);

    // Generate stock message with enhanced information
    const stockMessage = useMemo(() => {
        const errorMessage = getErrorMessage();
        if (errorMessage) {
            return errorMessage;
        }

        if (!isSalable) {
            return getInsufficientStockMessage();
        }

        return '';
    }, [getErrorMessage, isSalable, getInsufficientStockMessage]);

    const handleUpdateToAvailableQuantity = useCallback(() => {
        if (!availableQuantity) {
            return;
        }
        handleUpdateQuantity({
            item,
            value: availableQuantity,
        });
    }, [availableQuantity, handleUpdateQuantity, item]);

    if (!stockMessage) {
        return null;
    }

    return (
        <div className={styles.container}>
            <Text className={styles.stockMessage}>{stockMessage}</Text>
            <Button
                variant={'text'}
                showIf={availableQuantity !== null && availableQuantity > 0}
                className={styles.stockButton}
                onClick={handleUpdateToAvailableQuantity}
            >
                {t('quote_text_adjust')}
            </Button>
        </div>
    );
};
